import axios, { type AxiosInstance } from "axios";
import { config } from "@/Common/envConfig";
import { APP_ROUTES } from "@/Generated/Routes/paths";

import AppRouter from "@/Router";

import { useAuthStore } from "@/Modules/Auth/Store";

import { isValidString } from "@/Utils/String";

export interface ApiResponse<TModelApi> {
  body: TModelApi;
}

interface ResponseParams {
  raw?: boolean;
}

const client = axios.create({
  baseURL: config.apiUrl,
});

setRequestInterceptor(client);
setUnauthorizedInterceptor(client);

function setUnauthorizedInterceptor(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (axios.isAxiosError(error)) {
        const authStore = useAuthStore();

        const isAuthRoute = ([APP_ROUTES.login.root.name, APP_ROUTES.code.root.name] as string[]).includes(
          AppRouter.currentRoute().name as string
        );

        if (error.response?.status === 401 && !isAuthRoute) {
          authStore.logout();
        }

        return Promise.reject(error.response?.data);
      }
    }
  );
}

function setRequestInterceptor(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.request.use((request) => {
    if (!isValidString(request.headers.Authorization)) {
      const authStore = useAuthStore();
      const token = authStore.userToken ?? "";

      if (isValidString(token)) {
        request.headers.Authorization = `Bearer ${token}`;
      }
    }

    return request;
  });
}

export const get2 = async <TParams, TModel>(
  endpoint: string,
  params?: TParams,
  headers?: Record<string, string>,
  responseParams?: ResponseParams
): Promise<TModel> => {
  if (!responseParams?.raw) {
    const response = await client.get<ApiResponse<TModel>>(endpoint, {
      params,
      headers,
    });

    return response.data.body;
  } else {
    const response = await client.get<TModel>(endpoint, {
      params,
      headers,
    });

    return response.data;
  }
};

export const post2 = async <TBody, TModel>(
  endpoint: string,
  body?: TBody | null,
  headers?: Record<string, string>,
  responseParams?: ResponseParams
): Promise<TModel> => {
  if (!responseParams?.raw) {
    const response = await client.post<ApiResponse<TModel>>(endpoint, body, {
      headers,
    });

    return response.data.body;
  } else {
    const response = await client.post<TModel>(endpoint, body, {
      headers,
    });

    return response.data;
  }
};

export const patch2 = async <TBody, TModel>(
  endpoint: string,
  body?: TBody | null,
  headers?: Record<string, string>,
  responseParams?: ResponseParams
): Promise<TModel> => {
  if (!responseParams?.raw) {
    const response = await client.patch<ApiResponse<TModel>>(endpoint, body, {
      headers,
    });

    return response.data.body;
  } else {
    const response = await client.patch<TModel>(endpoint, body, {
      headers,
    });

    return response.data;
  }
};

export const remove2 = async <TParams, TModel>(
  endpoint: string,
  data?: TParams,
  headers?: Record<string, string>,
  responseParams?: ResponseParams
): Promise<TModel> => {
  if (!responseParams?.raw) {
    const response = await client.delete<ApiResponse<TModel>>(endpoint, {
      headers,
      data,
    });

    return response.data.body;
  } else {
    const response = await client.delete<TModel>(endpoint, {
      headers,
      data,
    });

    return response.data;
  }
};
